<template>
    <b-row class="mb-3">
        <b-card no-body bg-variant="white" class="w-100">
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0">캠페인 규칙 검색하기</h5>

                    <div v-if="loading">
                        <div class="d-flex align-items-center" style="height: 36px">
                            <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                            <p class="d-inline-block mb-0">조회중...</p>
                        </div>
                    </div>
                    <div v-else class="d-flex align-items-center">
                        <b-button form="campaign-rule-set-search-form"
                                  type="reset"
                                  class="mr-2"
                                  :disabled="loading"
                        >
                            {{ $t('초기화') }}
                        </b-button>
                        <b-button
                            variant="primary"
                            :disabled="loading"
                            form="campaign-rule-set-search-form"
                            type="submit"
                        >
                            {{ $t('검색하기') }}
                        </b-button>
                    </div>
                </div>
            </template>

            <div class="p-3">
                <b-form id="campaign-rule-set-search-form"
                        autocomplete="new-password"
                        @submit.stop.prevent="searchData"
                        @reset="resetForm"
                >
                    <b-row>
                        
                        <b-col cols="12" md="5" class="mb-3 mb-xl-0">
                            
                            <b-form-group
                                id="fieldset-input-project_id"
                                label="프로젝트"
                                label-for="input-project_id"
                                class="mb-0"
                            >
                                <div class="d-flex align-items-center rounded text-secondary w-100">
                                    <auto-complete-select ref="projectCodeAutoComplete"
                                                          v-model="searchFormValue.project_id"
                                                          :options="projectCodeOptions"
                                                          :loading="loading"
                                                          start-expanded
                                                          list-max-height="360px"
                                                          option-value-key="id"
                                                          :placeholder="$t('프로젝트를 선택해주세요.')"
                                                          autocomplete="new-password"
                                    >
                                    </auto-complete-select>
                                </div>
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="5" class="mb-3 mb-xl-0">
                            <b-form-group
                                id="fieldset-input-campaign_id"
                                label="캠페인"
                                label-for="input-campaign_id"
                                class="mb-0"
                            >
                                <div class="d-flex align-items-center rounded text-secondary w-100">
                                    <auto-complete-select ref="campaignAutoComplete"
                                                          v-model="searchFormValue.campaign_id"
                                                          :options="campaignIdOptions"
                                                          :loading="loading"
                                                          start-expanded
                                                          list-max-height="360px"
                                                          option-value-key="id"
                                                          :placeholder="$t('캠페인을 선택해주세요.')"
                                                          autocomplete="new-password"
                                    >
                                    </auto-complete-select>
                                </div>
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="2">
                            <b-form-group
                                id="fieldset-input-is_active"
                                label="활성 상태"
                                label-for="input-is_active"
                                class="mb-0"
                            >
                                <b-form-select id="input-is_active"
                                               v-model="searchFormValue.is_active"
                                               :disabled="loading"
                                >
                                    <b-form-select-option v-for="data in activeFlagOptions"
                                                          :key="`${data.text}-${data.value}`"
                                                          :value="data.value">{{ data.text }}
                                    </b-form-select-option>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-form>

            </div>
        </b-card>


        <b-row class="mt-3">
            <b-col cols="12">
                <b-card
                    :class="[{ 'shadow-sm': searchResults || (!loading && searchResults) }, 'list-container']"
                    :bg-variant="!searchResults ? 'light' : 'white'"
                    no-body
                >
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 class="mb-0">캠페인 규칙셋 목록</h6>
                            </div>
                            <div class="d-flex align-items-center">
                                <b-button variant="outline-secondary" size="md" class="mr-2"
                                          :disabled="loading"
                                          @click="openPresetModal">
                                    프리셋으로 추가
                                </b-button>
                                <b-button variant="outline-primary" size="md"
                                          @click="openCampaignRuleSetModal('add')">
                                    캠페인 규칙셋 추가
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <div class="table-container">
                        <b-table
                            fixed responsive
                            :items="searchResults"
                            :fields="tableColumns"
                            class="mb-0"
                            show-empty
                            :busy="loading"
                        >
                            <template #empty>
                                <PageEmptyComponent
                                    :text="$t('조회된 캠페인 규칙셋이 없습니다.')"
                                    :loading="loading"
                                    :loadingText="$t('캠페인 규칙셋 목록을 조회 중입니다.')"
                                />
                            </template>
                            <template #cell(readiness)="row">
                                <b-badge
                                    v-if="readinessByProject && readinessByProject[row.item.project_id]"
                                    v-b-tooltip.hover="readinessTooltip(row.item.project_id)"
                                    :variant="readinessByProject[row.item.project_id].ready ? 'success' : 'danger'"
                                >
                                    {{ readinessByProject[row.item.project_id].ready ? $t('가능') : $t('설정 미완료') }}
                                </b-badge>
                                <span v-else class="text-secondary">-</span>
                            </template>

                            <template #cell(name)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(row.value)">{{ row.value }}</span>
                            </template>

                            <template #cell(project_id)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(getTargetProjectText(row.value))">{{ row.value }}</span>
                            </template>

                            <template #cell(campaign_id)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(getTargetCampaignText(row.value))">{{ row.value }}</span>
                            </template>

                            <template #cell(root_rule_group)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(row.value.name)">{{ row.value.name }}</span>
                            </template>

                            <template #cell(is_active)="row">
                                <b-badge :variant="row.value ? 'success' : null">{{
                                        getActiveName(row.value)
                                    }}
                                </b-badge>
                            </template>

                            <template #cell(detail)="data">
                                <b-btn size="sm"
                                       variant="outline-primary"
                                       @click="openCampaignRuleSetModal('detail', data.item)">상세
                                </b-btn>
                            </template>

                            <template #cell(updated_at)="row">
                                {{ get_date(row.value) }}

                            </template>
                            <template #cell(created_at)="row">
                                {{ get_date(row.value) }}
                            </template>

                        </b-table>

                        <div class="pagination-container">
                            <b-pagination
                                ref="pagination"
                                v-model="pagination.currentPage"
                                :total-rows="pagination.totalRows"
                                :per-page="pagination.perPage"
                                first-number
                                last-number
                                :disabled="loading"
                                align="fill"
                                size="sm"
                                @change="(page)=>changeQuery({page})"
                            ></b-pagination>
                            <b-form-select
                                v-model="pagination.perPage"
                                class="ml-3 mb-3"
                                style="width: 100px"
                                size="sm"
                                :disabled="loading"
                                @change="(pageSize)=>changeQuery({pageSize})"
                            >
                                <b-form-select-option v-for="data in perPageOptions"
                                                      :key="`${data.text}-${data.value}`"
                                                      :value="data.value">{{ data.text }}
                                </b-form-select-option>
                            </b-form-select>

                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <CampaignRuleSetModal ref="campaignRuleSetModal"
                              :detail-id="detailId"
                              :campaigns="campaigns"
                              :projects="projects"
                              @updateDetailData="updateDetailData"
                              @addData="initPage"
                              @deleteData="initPage"
        />

        <CampaignRuleSetPresetModal :projects="projects"
                                    :campaigns="campaigns"
                                    @created="initPage"
        />

    </b-row>
</template>

<script>
import {listCampaignRuleSets} from '@shared_utils/api/ruleEngine';
import {getCampaignReadiness} from '@shared_utils/api/projectSchema';
import {
    getCampaignOptions,
    getDefaultPagination, getProjectCodeOptions, getTargetCampaignText, getTargetProjectText,
    onTooltipShow
} from '@shared/components/admin/pages/Management/common-data';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import PaginationPageMixin from '@shared/mixin/pagination-page-mixins';
import {
    columns,
    searchFormValue,
    activeFlagOptions
} from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/data';
import CampaignRuleSetModal
    from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/CampaignRuleSetModal.vue';
import CampaignRuleSetPresetModal
    from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/CampaignRuleSetPresetModal.vue';
import {mapState} from 'vuex';
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';

export default {
    name: 'ManageCampaignRuleSet',
    components: {
        AutoCompleteSelect,
        CampaignRuleSetModal,
        CampaignRuleSetPresetModal,
        PageEmptyComponent,
    },
    mixins: [PaginationPageMixin({searchForm: searchFormValue, pagination: getDefaultPagination(1, 20), booleanSelectKeys: ['is_active']})],
    props: {
        currentTab: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            tableColumns: columns,
            detailId: null,
            dataLoading: false,
            // project_id -> campaign-readiness 응답. 배지 셀이 렌더 시점에
            // 이 객체를 조회하므로 반드시 {} 로 초기화해야 한다. (searchResults 등은
            // PaginationPageMixin 이 제공하지만, 이 필드는 이 컴포넌트 소유다.)
            readinessByProject: {},

            projects: null,
            selectedProjectCodeId: null,
            campaigns: null,
            selectedCampaignId: null,
        };
    },
    computed: {
        ...mapState(['user_id']),
        loading() {
            return this.listLoading || this.dataLoading;
        },
        activeFlagOptions() {
            return activeFlagOptions;
        },
        getActiveName() {
            return (value) => {
                return value ? this.$t('활성') : this.$t('비활성');
            };
        },
        projectCodeOptions() {
            return getProjectCodeOptions(this.projects);
        },
        campaignIdOptions() {
            return getCampaignOptions(this.campaigns);
        },
        getTargetProjectText() {
            return (project_id) => {
                return getTargetProjectText(project_id, this.projects);
            };
        },
        getTargetCampaignText() {
            return (campaign_id) => {
                return getTargetCampaignText(campaign_id, this.campaigns);
            };
        },
    },
    watch: {
        '$route': {
            async handler() {
                if (this.$route.query.tab === 'campaignRule') {
                    this.dataLoading = true;

                    this.setSearchDataFormQuery();
                    try {
                        await Promise.all([
                            this.fetchData(),
                            this.getProjectCode(),
                            this.getCampaigns()
                        ]);
                    } catch (e) {
                        console.error(e);
                    } finally {
                        this.dataLoading = false;
                    }
                }
                else {
                    this.projects = null;
                    this.campaigns = null;
                }
            },
            // 직접 URL(?tab=campaignRule) 로 진입하면 route "변경" 이 없어 handler 가
            // 안 돌아 목록이 비어 있었다. 마운트 시에도 한 번 판정하게 한다.
            immediate: true
        }
    },
    methods: {
        onTooltipShow,
        openPresetModal() {
            this.$bvModal.show('campaign-rule-set-preset-modal');
        },
        async getProjectCode() {
            if (this.projects) {
                return;
            }
            try {
                const response = await this.$axios.get('/project_code');
                this.projects = response.data;
                setTimeout(()=>{
                    this.$refs.projectCodeAutoComplete.expandAllGroups();
                }, 300);
            } catch (error) {
                alert('죄송합니다. 프로젝트 코드 데이터를 불러오는 도중 장애라 발생했습니다. 개발팀에게 문의주시기 바랍니다.');
            }
        },
        async getCampaigns() {
            if (this.campaigns) {
                return;
            }
            try {
                this.isCampaignLoading = true;
                const response = await this.$axios.get('/read_campaigns_industries', {
                    params: {user_id: this.user_id}
                });
                this.campaigns = response.data;
            } catch (error) {
                alert('죄송합니다. 캠페인 데이터를 불러오는 도중 장애가 발생했습니다. 개발팀에게 문의주시기 바랍니다.');
            }
        },
        async searchData() {
            this.pagination.currentPage = 1;
            const checkSameQuery = await this.changeQuery({});
            if (checkSameQuery) {
                await this.fetchData();
            }
        },
        initPage() {
            this.resetForm();
            this.setSearchDataFormQuery();
            this.fetchData();
        },
        /**
         * 목록에 뜬 프로젝트들의 "신청 가능" 상태를 조회한다.
         *
         * 규칙셋 단위가 아니라 **프로젝트 단위** 판정이므로 project_id 로 중복을
         * 제거해 호출한다. 실패해도 목록 자체는 보여야 하므로 조용히 넘어간다.
         */
        async fetchReadiness(ruleSets) {
            const projectIds = [...new Set((ruleSets || []).map(item => item.project_id).filter(Boolean))];

            await Promise.all(projectIds.map(async projectId => {
                try {
                    const {data} = await getCampaignReadiness(projectId);
                    this.$set(this.readinessByProject, projectId, data);
                } catch (e) {
                    // 배지를 못 그릴 뿐, 목록 조회 자체를 실패시키지 않는다.
                }
            }));
        },
        readinessTooltip(projectId) {
            const readiness = this.readinessByProject[projectId];
            if (!readiness) return '';
            if (readiness.ready) return this.$t('이 프로젝트로 평가 신청이 가능합니다.');

            if (readiness.reason === 'missing_input_keys') {
                return this.$t('규칙이 참조하는 입력키가 등록되지 않았습니다') + ': '
                    + (readiness.missing_keys || []).join(', ')
                    + ' — ' + this.$t('[프로젝트 스키마 관리 > 스키마 입력 값 관리] 에서 등록해주세요.');
            }
            if (readiness.reason === 'no_campaign_mapping') {
                return this.$t('활성 규칙셋도 없고 프로젝트에 캠페인도 연결되어 있지 않습니다.');
            }
            if (readiness.reason === 'no_required_keys') {
                return this.$t('규칙셋에 조건이 하나도 없어 캠페인을 판별할 수 없습니다.');
            }
            return this.$t('평가 신청이 불가능한 상태입니다.');
        },
        async fetchData() {
            try {
                this.listLoading = true;

                console.log(this.searchFormValue);

                const res = await listCampaignRuleSets({
                    params: {
                        ...this.searchFormValue,
                        page: this.pagination.currentPage,
                        per_page: this.pagination.perPage,
                    },
                });

                if (res.data == null) {
                    this.$EventBus.$emit('toast-success', this.$t('검색 완료'), '프로젝트 스키마 검색 결과가 없습니다.');
                    return;
                }
                const {campaign_rule_sets, total, current_page} = res.data;

                this.searchResults = campaign_rule_sets.map((item, index) => {
                    const data = {
                        ...item,
                        value: JSON.stringify(item.value),
                        index: total - (this.pagination.perPage * (this.pagination.currentPage - 1) + index),
                    };
                    return data;
                });

                this.fetchReadiness(campaign_rule_sets);

                this.pagination.totalRows = total;
                this.pagination.currentPage = null;
                setTimeout(() => {
                    this.pagination.currentPage = Number(current_page);
                });
            } catch (error) {
                this.$EventBus.$emit('toast-failure', this.$t('검색 오류'), '캠페인 규칙셋 검색 도중 오류가 발생했습니다. 잠시후 재시도 해주세요.');
            } finally {
                this.listLoading = false;
            }
        },
        async openCampaignRuleSetModal(mode, item) {
            if (mode === 'add') {
                this.detailId = null;
                this.$refs.campaignRuleSetModal.changeMode(mode);
                this.$bvModal.show('campaign-rule-set-detail-modal');
                return;
            }


            this.detailId = item.id;
            this.$refs.campaignRuleSetModal.changeMode(mode);
            this.$bvModal.show('campaign-rule-set-detail-modal');

        },
        updateDetailData(updatedItem) {
            const idx = this.searchResults.findIndex((item) => {
                return item.id === updatedItem.id;
            });
            this.$set(this.searchResults, idx, {...updatedItem, index: this.searchResults.length - idx});
        }
    }
};
</script>

<style lang="scss">
</style>
